import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, Clock, ArrowRight, TrendingUp, Shield,
  Cloud, Bot, DollarSign, Sparkles, User, RefreshCw,
  ExternalLink, Newspaper, Zap, Globe, Loader2
} from 'lucide-react';
import axios from 'axios';
import NewsletterSignup from '../components/NewsletterSignup';
import PublicNavbar from '../components/PublicNavbar';
import { trackEvent } from '../hooks/useAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Blog = ({ isInternal = false }) => {
  const [liveNews, setLiveNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [newsCategory, setNewsCategory] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);
  // Iter78 — Blog post category filter (was previously a dead UI element).
  const [selectedCategory, setSelectedCategory] = useState('All');
  // Ref to the post-grid container so we can scroll the user TO the filter result
  // after they tap a pill. Without this the pill toggles ~600px above where the
  // posts actually live — making the filter feel "unresponsive" (real customer
  // complaint, Feb 16 2026).
  const postsGridRef = useRef(null);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    // Let React render first, then ease the user into the result.
    window.requestAnimationFrame(() => {
      if (postsGridRef.current) {
        postsGridRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  };

  // Fetch live news
  const fetchNews = async (category = 'all') => {
    setNewsLoading(true);
    try {
      const endpoint = category === 'all' ? '/api/news/all' : `/api/news/${category}`;
      const response = await axios.get(`${API_URL}${endpoint}?limit=12`);
      if (response.data.success) {
        setLiveNews(response.data.articles);
        setLastUpdated(new Date().toLocaleTimeString());
      }
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setNewsLoading(false);
    }
  };

  useEffect(() => {
    fetchNews(newsCategory);
    // Auto-refresh every 5 minutes
    const interval = setInterval(() => fetchNews(newsCategory), 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [newsCategory]);

  const featuredPost = {
    title: 'How to Save $487K on Snowflake in 30 Days: A Replicable Playbook',
    excerpt: 'A composite case study from 3 real Snowflake audits — 5 waste patterns, 30 days of fixes, and exactly which SQL statements moved the needle.',
    category: 'Strategy',
    date: 'Apr 25, 2026',
    readTime: '8 min read',
    author: 'Lakshmi Kiranmai Guduru',
    slug: 'snowflake-487k-savings-playbook'
  };

  const blogPosts = [
    // ===== Feb 2026 CSV-audit + evergreen SEO series =====
    {
      title: 'How to Audit Your AWS Bill in 60 Seconds (CSV Walkthrough · Feb 2026)',
      excerpt: 'The simplest AWS cost audit that exists. Download your Cost & Usage CSV, find the top 5 leaks in 60 seconds. Step-by-step with exact line items, columns, and queries.',
      category: 'AWS Cost',
      date: 'Feb 14, 2026',
      readTime: '8 min read',
      slug: 'how-to-audit-aws-bill-csv',
      icon: Cloud,
      color: 'text-orange-500',
      bg: 'bg-orange-500/10 border border-orange-500/30'
    },
    {
      title: 'How to Audit Your Azure Bill: The 60-Second CSV Walkthrough',
      excerpt: 'Most Azure bills hide 25–35% waste in 6 line items. Here\'s the CSV audit, the columns that matter, and the 5 Azure-specific patterns we see on every audit.',
      category: 'Cloud Tips',
      date: 'Feb 14, 2026',
      readTime: '8 min read',
      slug: 'how-to-audit-azure-bill-csv',
      icon: Cloud,
      color: 'text-sky-400',
      bg: 'bg-sky-500/10 border border-sky-500/30'
    },
    {
      title: 'How to Audit Your Google Cloud Bill: A Practical CSV Walkthrough',
      excerpt: 'GCP bills look cleaner than AWS — and hide 5 expensive patterns. Here\'s the CSV audit, the SKU codes to filter on, and what to fix first.',
      category: 'Cloud Tips',
      date: 'Feb 14, 2026',
      readTime: '7 min read',
      slug: 'how-to-audit-gcp-bill-csv',
      icon: Cloud,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10 border border-amber-500/30'
    },
    {
      title: 'How to Audit Your Snowflake Bill from a CSV Export',
      excerpt: 'A Snowflake CSV from Admin → Usage → the top 5 credit-leak warehouses in 60 seconds. The columns that matter, the patterns that hide $/month, and the SQL to confirm.',
      category: 'Best Practices',
      date: 'Feb 14, 2026',
      readTime: '7 min read',
      slug: 'how-to-audit-snowflake-bill-csv',
      icon: DollarSign,
      color: 'text-cyan-400',
      bg: 'bg-cyan-500/10 border border-cyan-500/30'
    },
    {
      title: 'How to Audit Your Databricks Bill from the Usage CSV',
      excerpt: 'Databricks bills are notoriously opaque. The CSV audit that shows the 5 DBU leaks even Cost Analytics misses — in 60 seconds.',
      category: 'Best Practices',
      date: 'Feb 14, 2026',
      readTime: '8 min read',
      slug: 'how-to-audit-databricks-bill-csv',
      icon: TrendingUp,
      color: 'text-rose-400',
      bg: 'bg-rose-500/10 border border-rose-500/30'
    },
    {
      title: 'How to Audit Your DigitalOcean Bill in 60 Seconds',
      excerpt: 'DigitalOcean is cheaper than AWS — until it isn\'t. The CSV audit that finds the orphan droplets, over-tiered databases, and bandwidth surprises.',
      category: 'Cloud Tips',
      date: 'Feb 14, 2026',
      readTime: '6 min read',
      slug: 'how-to-audit-digitalocean-bill-csv',
      icon: Cloud,
      color: 'text-blue-400',
      bg: 'bg-blue-500/10 border border-blue-500/30'
    },
    {
      title: 'LLM Token Pricing Guide: Feb 2026 Edition',
      excerpt: 'All the major LLM prices in one place. GPT-5.2, Claude Sonnet 4.5, Gemini 3, Llama 3.3 — per-million-token math for every model. Updated weekly.',
      category: 'AI Infrastructure',
      date: 'Feb 14, 2026',
      readTime: '9 min read',
      slug: 'llm-token-pricing-guide-2026',
      icon: Bot,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10 border border-emerald-500/30',
      cornerstone: true
    },
    {
      title: 'What FinOps Actually Means in 2026 (and Why It\'s Not About Spreadsheets Anymore)',
      excerpt: 'FinOps has shifted from manual cost reviews to real-time, AI-driven decisions. Here\'s what changed in the last 24 months — and what FinOps teams actually do now.',
      category: 'Strategy',
      date: 'Feb 14, 2026',
      readTime: '8 min read',
      slug: 'what-is-finops-in-2026',
      icon: Sparkles,
      color: 'text-amber-400',
      bg: 'bg-amber-500/10 border border-amber-500/30',
      cornerstone: true
    },
    {
      title: 'How CFOs Actually Read a Cloud Bill (Without Being a Cloud Engineer)',
      excerpt: 'The 7 questions every CFO should ask before approving the cloud line item. Sample dashboards, red-flag patterns, and the metrics that matter in a board pack.',
      category: 'Strategy',
      date: 'Feb 14, 2026',
      readTime: '9 min read',
      slug: 'how-cfos-read-a-cloud-bill',
      icon: DollarSign,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10 border border-emerald-500/30',
      cornerstone: true
    },
    // ===== Existing posts =====
    {
      title: 'The Datadog Cost Optimization Guide: 8 Patterns That Cut Bills 45%',
      excerpt: 'Datadog bills sneak up. Most teams pay 40-60% more than they need because of three settings nobody changes. The 8 patterns we use to cut Datadog bills nearly in half.',
      category: 'Best Practices',
      date: 'May 2, 2026',
      readTime: '10 min read',
      slug: 'datadog-cost-optimization-guide',
      icon: TrendingUp,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      cornerstone: true
    },
    {
      title: 'BigQuery Slot Tuning: How to Cut Query Costs 60% Without Slowing Anyone Down',
      excerpt: 'BigQuery on-demand pricing is a tax. Reservations + slot autoscaling cuts costs 40-70%, but only if you size them right. The slot-tuning playbook with real numbers.',
      category: 'Best Practices',
      date: 'May 2, 2026',
      readTime: '11 min read',
      slug: 'bigquery-slot-tuning-guide',
      icon: DollarSign,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10 border border-amber-500/30',
      cornerstone: true
    },
    {
      title: 'The Postgres RDS Cost Playbook: 10 Optimizations That Cut Bills 50%',
      excerpt: 'Postgres RDS is the most over-provisioned service in AWS. Wrong instance class, wrong storage, wrong backup retention — most teams overpay 40-60%. The 10-pattern playbook.',
      category: 'Best Practices',
      date: 'May 2, 2026',
      readTime: '12 min read',
      slug: 'postgres-rds-cost-playbook',
      icon: BookOpen,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      cornerstone: true
    },
    {
      title: 'The EC2 Right-Sizing Playbook: Cut $200K/Year From a 600-Instance Fleet',
      excerpt: 'Most EC2 fleets run at 12-18% average CPU. Right-sizing is the single biggest lever in AWS — bigger than RIs, bigger than Spot. The 6-step playbook from 9 audits.',
      category: 'Best Practices',
      date: 'May 2, 2026',
      readTime: '14 min read',
      slug: 'ec2-right-sizing-playbook',
      icon: BookOpen,
      color: 'text-orange-500',
      bg: 'bg-orange-500/10 border border-orange-500/30',
      cornerstone: true
    },
    {
      title: 'The 9 Lambda Cost Traps That Quietly Triple Your AWS Bill',
      excerpt: 'Lambda is supposed to be the cheap option. But provisioned concurrency, oversized memory, recursive triggers, and 6 other traps mean most teams overpay 2-3×. The field guide.',
      category: 'Best Practices',
      date: 'May 2, 2026',
      readTime: '13 min read',
      slug: 'lambda-cost-traps',
      icon: BookOpen,
      color: 'text-yellow-500',
      bg: 'bg-yellow-500/10 border border-yellow-500/30',
      cornerstone: true
    },
    {
      title: 'Redshift vs Snowflake in 2026: A Cost Decision Framework',
      excerpt: 'We\'ve helped 11 teams pick between them, and migrated 4 in each direction. The right answer depends on 5 specific signals — not what your AWS rep tells you.',
      category: 'Strategy',
      date: 'May 2, 2026',
      readTime: '12 min read',
      slug: 'redshift-vs-snowflake',
      icon: BookOpen,
      color: 'text-rose-500',
      bg: 'bg-rose-500/10 border border-rose-500/30',
      cornerstone: true
    },
    {
      title: 'DynamoDB Hot Partitions: How a Bad Key Turned $800/mo into $18K/mo',
      excerpt: 'Hot partitions are the #1 DynamoDB cost surprise nobody warns you about. A single bad key can 20× your bill. The diagnostic playbook — with real CloudWatch queries.',
      category: 'Best Practices',
      date: 'May 3, 2026',
      readTime: '11 min read',
      slug: 'dynamodb-hot-partition-cost-playbook',
      icon: BookOpen,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10 border border-emerald-500/30',
      cornerstone: true
    },
    {
      title: 'Pinecone Cost Optimization: 7 Patterns That Cut Vector-DB Bills 50% (2026)',
      excerpt: 'Vector databases blew through engineering budgets in 2024. This is the field guide we wish existed: 7 audited Pinecone optimization patterns ranked by ROI.',
      category: 'AI Infrastructure',
      date: 'May 7, 2026',
      readTime: '11 min read',
      slug: 'pinecone-cost-optimization-guide',
      icon: BookOpen,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      cornerstone: true,
    },
    {
      title: 'ElastiCache Cost Optimization: 8 Patterns That Cut Redis Bills 45%',
      excerpt: 'ElastiCache pricing punishes the obvious mistakes. The good news: most ElastiCache waste is fixable in a single afternoon — here are the 8 patterns we audit.',
      category: 'AWS Cost',
      date: 'May 7, 2026',
      readTime: '10 min read',
      slug: 'elasticache-redis-cost-optimization-guide',
      icon: DollarSign,
      color: 'text-rose-500',
      bg: 'bg-rose-500/10 border border-rose-500/30',
      cornerstone: true,
    },
    {
      title: 'S3 Egress: The Hidden $0.09/GB That Eats AI Companies Alive',
      excerpt: "S3 egress is the line item most engineering teams don't see until it's catastrophic. Here are the 6 patterns that cut S3 egress bills 70% — exact, audited, real.",
      category: 'AWS Cost',
      date: 'May 7, 2026',
      readTime: '12 min read',
      slug: 's3-egress-cost-optimization-guide',
      icon: BookOpen,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10 border border-amber-500/30',
      cornerstone: true,
    },
    {
      title: 'The Airflow & Composer Cost Playbook: Cut Orchestration Bills 60%',
      excerpt: 'Most data teams overpay on Airflow/Composer by 40-70%. Oversized workers, idle schedulers, and noisy DAGs compound. The 8-pattern playbook that halves the bill.',
      category: 'Best Practices',
      date: 'May 3, 2026',
      readTime: '12 min read',
      slug: 'airflow-composer-cost-playbook',
      icon: BookOpen,
      color: 'text-indigo-500',
      bg: 'bg-indigo-500/10 border border-indigo-500/30',
      cornerstone: true
    },
    {
      title: 'The Kubernetes Cost Allocation Guide: How to Tag, Track, and Charge Back Every Pod',
      excerpt: 'Kubernetes is great at hiding cost. This guide shows you exactly how to allocate every dollar of your cluster spend — with copy-paste YAML and 4 query patterns most teams miss.',
      category: 'Best Practices',
      date: 'May 2, 2026',
      readTime: '10 min read',
      slug: 'kubernetes-cost-allocation-guide',
      icon: BookOpen,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      cornerstone: true
    },
    {
      title: 'MongoDB Atlas Cost Optimization: 9 Patterns That Cut Bills 50%',
      excerpt: 'Most teams overpay on Atlas by 40-60% because of three settings nobody touches. The 9 patterns we use to cut Atlas bills in half — without migrating off.',
      category: 'Best Practices',
      date: 'May 2, 2026',
      readTime: '10 min read',
      slug: 'mongodb-atlas-cost-optimization-guide',
      icon: DollarSign,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10 border border-emerald-500/30',
      cornerstone: true
    },
    {
      title: 'Anatomy of a $100K AWS Bill: What Most CFOs Miss',
      excerpt: 'A line-by-line dissection of a real $100K/month AWS invoice — the 7 hidden multipliers nobody catches, and the 4 questions every CFO should ask before approving next month.',
      category: 'Strategy',
      date: 'May 2, 2026',
      readTime: '11 min read',
      slug: 'anatomy-of-a-100k-aws-bill',
      icon: DollarSign,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10 border border-amber-500/30',
      cornerstone: true
    },
    {
      title: 'Why 67% of SaaS Seats Are Wasted (And the 5-Step System to Find Yours)',
      excerpt: 'After auditing 200+ SaaS stacks, the same pattern: ~2 in 3 paid seats are inactive, duplicated, or never used. The 5-step audit system that recovers $4K-$15K/month.',
      category: 'Strategy',
      date: 'May 2, 2026',
      readTime: '9 min read',
      slug: 'why-67-percent-of-saas-seats-are-wasted',
      icon: TrendingUp,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      cornerstone: true
    },
    {
      title: 'The 8 FinOps Dashboard Patterns That Actually Drive Decisions (Most Tools Miss 5 of Them)',
      excerpt: 'After 12 months of dashboard reviews with FinOps teams, the same 8 patterns separate dashboards that drive cost decisions from those that just look pretty. Here\'s the blueprint.',
      category: 'Best Practices',
      date: 'May 1, 2026',
      readTime: '9 min read',
      slug: 'finops-dashboard-design-patterns',
      icon: TrendingUp,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10'
    },
    {
      title: 'The 6 Hidden Costs of Databricks Nobody Tells You About (Until Your DBU Bill Doubles)',
      excerpt: 'Photon at 2x DBUs, Serverless premium markup, idle cluster minutes, network egress, default cluster sizes — the 6 silent multipliers turning a $40K Databricks bill into $90K.',
      category: 'Cloud Tips',
      date: 'May 1, 2026',
      readTime: '7 min read',
      slug: 'databricks-hidden-costs-dbu-multiplier',
      icon: Bot,
      color: 'text-orange-500',
      bg: 'bg-orange-500/10 border border-orange-500/30'
    },
    {
      title: 'Cloud Cost Anomaly Detection: Why MTTD Matters More Than Total Spend',
      excerpt: 'A $48K AWS overspend caught in 23 minutes vs 8 days is the difference between a non-event and a board-level conversation. Here\'s how to build anomaly detection that actually catches things in time.',
      category: 'Best Practices',
      date: 'May 1, 2026',
      readTime: '8 min read',
      slug: 'cloud-cost-anomaly-detection-guide',
      icon: DollarSign,
      color: 'text-rose-500',
      bg: 'bg-rose-500/10 border border-rose-500/30'
    },
    {
      title: 'The Snowflake Cost Optimization Playbook: 7 Patterns That Cut Your Bill 40%',
      excerpt: 'After auditing dozens of Snowflake accounts, the same 7 patterns drain budgets over and over. Here\'s the exact playbook with copy-pasteable SQL fixes.',
      category: 'Best Practices',
      date: 'Apr 25, 2026',
      readTime: '8 min read',
      slug: 'snowflake-cost-optimization-playbook',
      icon: DollarSign,
      color: 'text-emerald-500',
      bg: 'bg-emerald-500/10 border border-emerald-500/30',
      cornerstone: true
    },
    {
      title: 'Why Your Snowflake Warehouse Is Burning Cash While You Sleep',
      excerpt: 'A Medium warehouse left always-on burns ~$208 every 8 hours of idle time. Here\'s how to diagnose it in 60 seconds — and the one SQL statement that fixes it.',
      category: 'Cloud Tips',
      date: 'Apr 23, 2026',
      readTime: '5 min read',
      slug: 'snowflake-warehouse-burning-cash-overnight',
      icon: Cloud,
      color: 'text-sky-500',
      bg: 'bg-sky-500/10 border border-sky-500/30'
    },
    {
      title: 'Databricks DBU Reduction: A Data Engineer\'s Guide to Cutting Spend 40%',
      excerpt: '7 patterns we see in every Databricks workspace audit — Photon misuse, always-on clusters, over-provisioned drivers, and the diagnostic SQL to find them.',
      category: 'Best Practices',
      date: 'Apr 21, 2026',
      readTime: '7 min read',
      slug: 'databricks-dbu-reduction-guide',
      icon: TrendingUp,
      color: 'text-amber-500',
      bg: 'bg-amber-500/10 border border-amber-500/30'
    },
    {
      title: 'The Hidden 32% Cloud Waste No FinOps Tool Is Showing You',
      excerpt: 'Most FinOps tools show you the bill. They don\'t show you the waste — especially in Snowflake, Databricks, SaaS sprawl, and egress. Here\'s what they\'re missing.',
      category: 'Strategy',
      date: 'Apr 18, 2026',
      readTime: '6 min read',
      slug: 'hidden-32-percent-cloud-waste',
      icon: Bot,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10'
    }
  ];

  const categories = ['All', 'Cloud Tips', 'Best Practices', 'AI & ML', 'Kubernetes', 'Security', 'Strategy', 'Company'];
  const newsCategories = [
    { id: 'all', label: 'All News', icon: Globe },
    { id: 'cloud', label: 'Cloud', icon: Cloud },
    { id: 'tech', label: 'Tech', icon: Zap }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      'AWS': 'bg-orange-500/15 text-orange-300 border border-orange-500/30',
      'Azure': 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
      'GCP': 'bg-rose-500/15 text-rose-300 border border-rose-500/30',
      'Tech': 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
      'Cloud': 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30',
    };
    return colors[category] || 'bg-slate-800/60 text-slate-300 border border-white/10';
  };

  return (
    <div className={`dark min-h-screen bg-slate-950 text-slate-100 ${isInternal ? 'pt-6' : ''}`} data-testid="blog-page">
      <SEOHead
        title="CARTIEAI Blog — FinOps, Cloud Cost, and AI Insights"
        description="Practical guides on cloud cost optimization, FinOps best practices, and AI-powered spend intelligence for Snowflake, Databricks, AWS, Azure, and GCP."
        canonical="https://cartieai.com/blog"
        keywords="FinOps blog, cloud cost blog, Snowflake optimization, AWS cost tips, cloud waste, FinOps case studies"
      />
      {/* v34.99 (Feb 14 2026) — Switched from a custom 5-link navbar (About/Blog/
          Guides/Careers/Contact) to <PublicNavbar /> so /blog matches /guides
          visually. The old navbar was light-themed (bg-white/80) clashing with
          the forced bg-slate-950 wrapper, which made the H1 + cards invisible. */}
      {!isInternal && <PublicNavbar />}

      {/* Hero Section */}
      <section className={`${isInternal ? 'pt-6 pb-8' : 'pt-32 pb-8'} px-4 sm:px-6 lg:px-8`}>
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="mb-6 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
            <BookOpen className="w-3 h-3 mr-1" />
            CARTIEAI Blog
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white mb-6">
            Insights & Ideas
          </h1>
          <p className="text-xl text-slate-400 dark:text-slate-400">
            Cloud costs, FinOps best practices, and the latest tech news.
          </p>
        </div>
      </section>

      {/* Live Tech News Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-50 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500 bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
                <Newspaper className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                  Live Tech News
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
                  </span>
                </h2>
                {lastUpdated && (
                  <p className="text-sm text-slate-400">Last updated: {lastUpdated}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* News Category Tabs */}
              <div className="flex bg-slate-800/60 dark:bg-slate-800 rounded-lg p-1">
                {newsCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setNewsCategory(cat.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                      newsCategory === cat.id
                        ? 'bg-slate-900/60 dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                        : 'text-slate-400 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                    }`}
                  >
                    <cat.icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchNews(newsCategory)}
                disabled={newsLoading}
                className="ml-2"
              >
                <RefreshCw className={`w-4 h-4 ${newsLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* News Grid */}
          {newsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              <span className="ml-3 text-slate-400 dark:text-slate-400">Loading latest news...</span>
            </div>
          ) : liveNews.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {liveNews.map((article, idx) => (
                <a
                  key={idx}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-slate-900/60 dark:bg-slate-800/50 rounded-xl border border-white/10 dark:border-slate-700 overflow-hidden hover:shadow-lg hover:border-emerald-300 dark:hover:border-emerald-700 transition-all"
                >
                  {article.image_url && (
                    <div className="aspect-video bg-slate-800/60 dark:bg-slate-800 overflow-hidden">
                      <img 
                        src={article.image_url} 
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge className={getCategoryColor(article.category)}>
                        {article.category}
                      </Badge>
                      <span className="text-xs text-slate-400">{article.source}</span>
                    </div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-2 line-clamp-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-sm text-slate-400 dark:text-slate-400 line-clamp-2 mb-3">
                      {article.description}
                    </p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>{article.published}</span>
                      <span className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400 group-hover:underline">
                        Read more <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Newspaper className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No news available at the moment. Try refreshing.</p>
            </div>
          )}
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-4">
        <hr className="border-white/10 dark:border-slate-800" />
      </div>

      {/* Our Blog Posts Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                From CARTIEAI
              </h2>
              <p className="text-sm text-slate-400">Our insights on cloud costs and FinOps</p>
            </div>
          </div>

          {/* Categories — make pills clearly clickable: select-none prevents text-selection
              hijacking the tap target (real customer complaint Feb 2026); cursor-pointer +
              active:scale-95 + scale animation on active state provides tactile feedback. */}
          <div ref={postsGridRef} className="flex flex-wrap items-center gap-2 mb-8 scroll-mt-24" data-testid="blog-categories">
            {categories.map((category) => {
              const isActive = category === selectedCategory;
              return (
                <button
                  key={category}
                  type="button"
                  onClick={() => handleCategoryChange(category)}
                  data-testid={`blog-category-${category.toLowerCase().replace(/\s+|&/g, '-').replace(/-+/g, '-')}`}
                  className={`select-none cursor-pointer px-4 py-2 rounded-full text-sm font-medium transition-all active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 ${
                    isActive
                      ? 'bg-emerald-600 text-white shadow-md shadow-emerald-500/40 scale-[1.03]'
                      : 'bg-slate-800/60 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:text-emerald-700 dark:hover:text-emerald-300 hover:scale-[1.02]'
                  }`}
                  aria-pressed={isActive}
                  style={{ WebkitTapHighlightColor: 'transparent' }}
                >
                  {category}
                </button>
              );
            })}
          </div>

          {/* Featured Post — select-none prevents text-selection from hijacking card click (real customer bug Feb 2026) */}
          <Link to={`/blog/${featuredPost.slug}`} className="block select-none cursor-pointer active:scale-[0.995] transition-transform" data-testid="blog-featured-card" style={{ WebkitTapHighlightColor: 'transparent' }}>
            <div className="bg-slate-900/60 border border-emerald-500/30 rounded-2xl p-8 mb-12 hover:border-emerald-400/60 hover:shadow-lg transition-all">
              <Badge className="mb-4 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400">
                Featured
              </Badge>
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white mb-4">
                {featuredPost.title}
              </h2>
              <p className="text-slate-400 dark:text-slate-400 mb-6 max-w-2xl">
                {featuredPost.excerpt}
              </p>
              <div className="flex items-center gap-4 text-sm text-slate-400">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {featuredPost.author}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {featuredPost.readTime}
                </span>
                <span>{featuredPost.date}</span>
              </div>
              <Button className="mt-6 bg-emerald-600 hover:bg-emerald-700 pointer-events-none">
                Read Full Story
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </Link>

          {/* 🔥 Cornerstone Reads — high-signal posts surfaced above the regular
               grid. Filtered by `cornerstone: true` flag on each post. Hidden when
               a category filter is active (they show inside the regular grid then). */}
          {selectedCategory === 'All' && blogPosts.some((p) => p.cornerstone) && (
            <div className="mb-12" data-testid="cornerstone-reads-section">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-500" />
                  <h2 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">
                    Cornerstone Reads
                  </h2>
                  <Badge className="ml-1 bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/30 text-[11px] tracking-widest font-bold">
                    DEEP DIVES
                  </Badge>
                </div>
                <span className="text-xs text-slate-400 dark:text-slate-400">
                  Most-cited, most-shared, most-actionable
                </span>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                {blogPosts
                  .filter((p) => p.cornerstone)
                  .map((post) => {
                    const Icon = post.icon || BookOpen;
                    return (
                      <div
                        key={`cornerstone-${post.slug}`}
                        className="group relative rounded-xl bg-white border border-amber-200/70 hover:border-amber-400 transition-all hover:shadow-lg hover:shadow-amber-500/10 hover:-translate-y-0.5 flex flex-col"
                      >
                        <Link
                          to={`/blog/${post.slug}`}
                          data-testid={`cornerstone-card-${post.slug}`}
                          onClick={() => trackEvent('cornerstone_click', { slug: post.slug, title: post.title })}
                          className="block p-5 flex-1 select-none cursor-pointer active:scale-[0.99] transition-transform"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="absolute top-3 right-3">
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-500/30 text-[11px] tracking-widest font-bold">
                              🔥 CORNERSTONE
                            </Badge>
                          </div>
                          <div className={`w-9 h-9 rounded-lg ${post.bg} flex items-center justify-center mb-3`}>
                            <Icon className={`w-4 h-4 ${post.color}`} />
                          </div>
                          <h3 className="text-base font-bold text-slate-950 mb-2 leading-tight pr-16 group-hover:text-amber-700 transition-colors line-clamp-2">
                            {post.title}
                          </h3>
                          <p className="text-xs text-neutral-700 leading-relaxed line-clamp-3 mb-3">
                            {post.excerpt}
                          </p>
                          <div className="flex items-center justify-between text-[11px] text-slate-500">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {post.readTime}
                            </span>
                            <span className="flex items-center gap-1 font-semibold text-amber-700 group-hover:gap-2 transition-all">
                              Read deep dive
                              <ArrowRight className="w-3 h-3" />
                            </span>
                          </div>
                        </Link>
                        {/* Funnel mini-CTA — second path from cornerstone → demo (Iter84k) */}
                        <div className="border-t border-amber-100 px-5 py-2.5 flex items-center justify-between">
                          <span className="text-[10.5px] text-slate-500">
                            Or skip ahead →
                          </span>
                          <Link
                            to="/dashboard"
                            data-testid={`cornerstone-demo-cta-${post.slug}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              trackEvent('cornerstone_demo_cta', { slug: post.slug });
                            }}
                            className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-600 inline-flex items-center gap-1"
                          >
                            Try the live demo
                            <ArrowRight className="w-2.5 h-2.5" />
                          </Link>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {/* Blog Posts Grid */}
          {(() => {
            const filteredPosts =
              selectedCategory === 'All'
                ? blogPosts.filter((p) => !p.cornerstone)  // dedupe — cornerstones already shown above
                : blogPosts.filter((p) => p.category === selectedCategory);
            if (filteredPosts.length === 0) {
              return (
                <div
                  className="text-center py-16 border border-dashed border-white/10 dark:border-slate-800 rounded-xl"
                  data-testid="blog-empty-state"
                >
                  <p className="text-slate-400 dark:text-slate-400 text-sm">
                    No posts in <span className="font-semibold text-slate-700 dark:text-slate-200">{selectedCategory}</span> yet — check back soon.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleCategoryChange('All')}
                    className="select-none cursor-pointer mt-4 text-sm font-medium text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 active:scale-95 transition-transform"
                    data-testid="blog-empty-reset"
                  >
                    ← View all posts
                  </button>
                </div>
              );
            }
            return (
              <div className="grid md:grid-cols-2 gap-6">
                {filteredPosts.map((post) => (
                  <Link
                    key={post.slug}
                    to={`/blog/${post.slug}`}
                    className="group p-6 bg-slate-900/60 dark:bg-slate-800/50 rounded-xl border border-white/10 dark:border-slate-700 hover:shadow-lg hover:border-emerald-300 dark:hover:border-emerald-700 transition-all cursor-pointer select-none block active:scale-[0.99]"
                    data-testid={`blog-card-${post.slug}`}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-start gap-4">
                      <div className={`p-3 rounded-lg ${post.bg}`}>
                        <post.icon className={`w-6 h-6 ${post.color}`} />
                      </div>
                      <div className="flex-1">
                        <Badge variant="secondary" className="mb-2 text-xs">
                          {post.category}
                        </Badge>
                        <h3 className="font-semibold text-slate-900 dark:text-white mb-2 group-hover:text-emerald-600 dark:group-hover:text-emerald-400">
                          {post.title}
                        </h3>
                        <p className="text-sm text-slate-400 dark:text-slate-400 mb-4">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between text-xs text-slate-400">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                          <span>{post.date}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            );
          })()}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/40 dark:bg-slate-900">
        <div className="max-w-2xl mx-auto">
          <NewsletterSignup variant="dark" source="blog_index" />
        </div>
      </section>

      {/* Footer */}
      {!isInternal && (
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-slate-900 text-slate-400">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Link to="/" className="flex items-center gap-2">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-8 h-8" />
              <span className="text-white font-bold">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
            </Link>
            <div className="flex items-center gap-6 text-sm">
              <Link to="/about" className="hover:text-white">About</Link>
              <Link to="/blog" className="hover:text-white">Blog</Link>
              <Link to="/careers" className="hover:text-white">Careers</Link>
              <Link to="/contact" className="hover:text-white">Contact</Link>
              <Link to="/privacy-policy" className="hover:text-white">Privacy</Link>
              <Link to="/terms" className="hover:text-white">Terms</Link>
            </div>
            <p className="text-sm" data-testid="blog-footer-entity">© 2026 CARTIEAI LLC · Texas, USA · All rights reserved.</p>
          </div>
        </div>
      </footer>
      )}
    </div>
  );
};

export default Blog;
